import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BrainCircuitIcon, PuzzleIcon, DollarSignIcon, CoinsIcon } from "lucide-react";
import { BudgetGridlock } from "./BudgetGridlock";
import { FinancialPuzzleGame } from "../FinancialPuzzleGame";

interface Puzzle {
  id: string;
  title: React.ReactNode;
  description: string;
  icon: React.ReactNode;
  difficulty: "Easy" | "Medium" | "Hard";
  estimatedTime: string;
}

export const FinancialPuzzlesView = () => {
  const [selectedPuzzle, setSelectedPuzzle] = useState<string | null>(null);

  const puzzles: Puzzle[] = [
    {
      id: "budget-gridlock",
      title: (
        <div className="flex items-center gap-2">
          <PuzzleIcon className="w-6 h-6 text-purple-500" />
          <span className="font-bold">Budget Gridlock</span>
        </div>
      ),
      description: "Arrange your expenses in a grid while staying within budget!",
      icon: <DollarSignIcon className="w-8 h-8" />,
      difficulty: "Medium",
      estimatedTime: "10 mins"
    },
    {
      id: "financial-quiz",
      title: (
        <div className="flex items-center gap-2">
          <BrainCircuitIcon className="w-6 h-6 text-blue-500" />
          <span className="font-bold">Financial IQ Challenge</span>
        </div>
      ),
      description: "Test your financial knowledge with challenging questions!",
      icon: <CoinsIcon className="w-8 h-8" />,
      difficulty: "Easy",
      estimatedTime: "5 mins"
    }
  ];

  const renderSelectedPuzzle = () => {
    switch (selectedPuzzle) {
      case "budget-gridlock":
        return <BudgetGridlock />;
      case "financial-quiz":
        return <FinancialPuzzleGame />;
      default:
        return null;
    }
  };

  if (selectedPuzzle) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedPuzzle(null)}
          className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
        >
          ← Back to Puzzles
        </button>
        {renderSelectedPuzzle()}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {puzzles.map((puzzle) => (
        <Card
          key={puzzle.id}
          className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
          onClick={() => setSelectedPuzzle(puzzle.id)}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              {puzzle.title}
              <span className={`
                text-sm px-2 py-1 rounded-full
                ${puzzle.difficulty === "Easy" ? "bg-green-100 text-green-700" :
                  puzzle.difficulty === "Medium" ? "bg-yellow-100 text-yellow-700" :
                    "bg-red-100 text-red-700"}
              `}>
                {puzzle.difficulty}
              </span>
            </CardTitle>
            <CardDescription>{puzzle.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <BrainCircuitIcon className="w-4 h-4" />
                {puzzle.estimatedTime}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};